import { useCurrentOrg, useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

interface WebhookSub {
  id: string;
  url: string;
  events: string[];
  isActive: boolean;
  lastDeliveredAt: string | null;
  createdAt: string;
}

interface Delivery {
  id: string;
  event: string;
  status: string;
  attempts: number;
  lastAttemptAt: string | null;
  lastResponseStatus: number | null;
  lastResponseBody: string | null;
  createdAt: string;
}

const ALL_EVENTS = ['analysis.ready', 'analysis.updated'] as const;

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    success: 'bg-green-50 text-green-700',
    failed: 'bg-red-50 text-red-600',
    dead: 'bg-slate-100 text-slate-500',
    pending: 'bg-yellow-50 text-yellow-700',
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[status] ?? 'bg-slate-100 text-slate-500'}`}
    >
      {status}
    </span>
  );
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function DeliveryLog({
  subId,
  getToken,
}: {
  subId: string;
  getToken: () => Promise<string | null>;
}) {
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  useEffect(() => {
    getToken().then(async (token) => {
      const res = await fetch(`${API}/api/v1/webhooks/${subId}/deliveries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = (await res.json()) as { deliveries: Delivery[]; nextCursor: string | null };
        setDeliveries(data.deliveries);
        setNextCursor(data.nextCursor);
      }
      setLoading(false);
    });
  }, [subId, getToken]);

  async function handleReplay(deliveryId: string) {
    const token = await getToken();
    await fetch(`${API}/api/v1/webhooks/${subId}/deliveries/${deliveryId}/replay`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    // Optimistically update status
    setDeliveries((prev) =>
      prev.map((d) => (d.id === deliveryId ? { ...d, status: 'pending' } : d)),
    );
  }

  if (loading) return <p className="py-2 text-xs text-slate-400">Loading…</p>;
  if (deliveries.length === 0) {
    return <p className="py-2 text-xs text-slate-400">No deliveries yet.</p>;
  }

  return (
    <div className="mt-2 overflow-hidden rounded border border-slate-200">
      <table className="min-w-full text-xs">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-3 py-1.5 text-left font-medium text-slate-500">Time</th>
            <th className="px-3 py-1.5 text-left font-medium text-slate-500">Event</th>
            <th className="px-3 py-1.5 text-left font-medium text-slate-500">Status</th>
            <th className="px-3 py-1.5 text-left font-medium text-slate-500">HTTP</th>
            <th className="px-3 py-1.5" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {deliveries.map((d) => (
            <tr key={d.id} className="hover:bg-slate-50">
              <td className="px-3 py-1.5 text-slate-500">{formatRelative(d.createdAt)}</td>
              <td className="px-3 py-1.5 font-mono text-slate-700">{d.event}</td>
              <td className="px-3 py-1.5">
                <StatusBadge status={d.status} />
              </td>
              <td className="px-3 py-1.5 text-slate-500">{d.lastResponseStatus ?? '—'}</td>
              <td className="px-3 py-1.5 text-right">
                {(d.status === 'failed' || d.status === 'dead') && (
                  <button
                    type="button"
                    onClick={() => handleReplay(d.id)}
                    className="text-blue-600 hover:underline"
                  >
                    Replay
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {nextCursor && (
        <div className="border-t border-slate-200 px-3 py-2 text-xs text-slate-400">
          Showing latest {deliveries.length} — older entries available
        </div>
      )}
    </div>
  );
}

export function SettingsWebhooksRoute() {
  const getToken = useSessionTokenGetter();
  const org = useCurrentOrg();

  const [subs, setSubs] = useState<WebhookSub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form
  const [url, setUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>(['analysis.ready']);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Revealed secret shown once
  const [revealedSecret, setRevealedSecret] = useState<{ id: string; secret: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // Expanded delivery logs
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  // Test toast
  const [testToast, setTestToast] = useState<string | null>(null);

  // Delete confirmation
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchSubs = useCallback(async () => {
    if (!org?.clerkOrgId) return [];
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/webhooks`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json() as Promise<WebhookSub[]>;
  }, [org?.clerkOrgId, getToken]);

  useEffect(() => {
    fetchSubs()
      .then((data) => setSubs(data))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [fetchSubs]);

  function toggleEvent(ev: string) {
    setSelectedEvents((prev) => (prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev]));
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim() || selectedEvents.length === 0) return;
    setCreating(true);
    setCreateError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/webhooks`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim(), events: selectedEvents }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        setCreateError(err.error ?? 'Failed to create webhook');
        return;
      }
      const created = (await res.json()) as { id: string; secret: string };
      setRevealedSecret({ id: created.id, secret: created.secret });
      setUrl('');
      setSelectedEvents(['analysis.ready']);
      setSubs(await fetchSubs());
    } catch (err) {
      setCreateError((err as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: string) {
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/webhooks/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setSubs((prev) => prev.filter((s) => s.id !== id));
      setDeleting(null);
    }
  }

  async function handleTest(id: string) {
    const token = await getToken();
    await fetch(`${API}/api/v1/webhooks/${id}/test`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    setTestToast(id);
    setTimeout(() => setTestToast(null), 3000);
  }

  function toggleLog(id: string) {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const activeSubs = subs.filter((s) => s.isActive);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-slate-400">
        Loading webhooks…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">Webhooks</h1>
        <p className="mt-1 text-sm text-slate-500">
          Receive real-time HTTP POST notifications when analyses are ready. Verify deliveries using
          the <code className="rounded bg-slate-100 px-1 text-xs">X-Develio-Signature</code>{' '}
          HMAC-SHA256 header.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Revealed secret banner */}
      {revealedSecret && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-semibold text-amber-800">
                Save your signing secret — it won't be shown again
              </p>
              <p className="mt-0.5 text-xs text-amber-700">
                Use it to verify{' '}
                <code className="rounded bg-amber-100 px-1">X-Develio-Signature</code> headers on
                incoming deliveries.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setRevealedSecret(null)}
              className="text-amber-600 hover:text-amber-800 text-lg leading-none"
            >
              ×
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 rounded border border-amber-200 bg-white px-3 py-2 font-mono text-sm text-slate-900 select-all break-all">
              {revealedSecret.secret}
            </code>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(revealedSecret.secret);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              className="shrink-0 rounded-md border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {/* Create form */}
      <form onSubmit={handleCreate} className="mb-6 rounded-lg border border-slate-200 p-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Add endpoint</h2>
        <div className="mb-3">
          <label
            htmlFor="webhook-endpoint-url"
            className="mb-1 block text-xs font-medium text-slate-600"
          >
            Endpoint URL (must be HTTPS)
          </label>
          <input
            id="webhook-endpoint-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://hooks.zapier.com/hooks/catch/..."
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            required
          />
        </div>
        <div className="mb-4">
          <p className="mb-2 text-xs font-medium text-slate-600">Events to receive</p>
          <div className="flex gap-4">
            {ALL_EVENTS.map((ev) => (
              <label
                key={ev}
                className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
              >
                <input
                  type="checkbox"
                  checked={selectedEvents.includes(ev)}
                  onChange={() => toggleEvent(ev)}
                  className="h-4 w-4 rounded border-slate-300"
                />
                <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{ev}</code>
              </label>
            ))}
          </div>
        </div>
        {createError && <p className="mb-3 text-xs text-red-600">{createError}</p>}
        <button
          type="submit"
          disabled={creating || !url.trim() || selectedEvents.length === 0}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {creating ? 'Creating…' : 'Create Webhook'}
        </button>
      </form>

      {/* Subscriptions list */}
      {activeSubs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 py-12 text-center">
          <p className="text-sm text-slate-500">No webhooks yet.</p>
          <p className="mt-1 text-xs text-slate-400">
            Add an endpoint above to receive real-time analysis events.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {activeSubs.map((sub) => (
            <div key={sub.id} className="rounded-lg border border-slate-200 p-4">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-mono text-sm text-slate-800">{sub.url}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {sub.events.map((ev) => (
                      <span
                        key={ev}
                        className="rounded bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-700"
                      >
                        {ev}
                      </span>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-slate-400">
                    Last delivery: {formatRelative(sub.lastDeliveredAt)} · Created{' '}
                    {new Date(sub.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="ml-4 flex shrink-0 items-center gap-3 text-sm">
                  {testToast === sub.id ? (
                    <span className="text-xs text-green-600">Test queued ✓</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleTest(sub.id)}
                      className="text-slate-500 hover:text-slate-800"
                    >
                      Test
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleLog(sub.id)}
                    className="text-slate-500 hover:text-slate-800"
                  >
                    {expandedLogs.has(sub.id) ? 'Hide log' : 'Log'}
                  </button>
                  {deleting === sub.id ? (
                    <span className="flex items-center gap-2 text-xs">
                      <span className="text-slate-600">Delete?</span>
                      <button
                        type="button"
                        onClick={() => handleDelete(sub.id)}
                        className="font-medium text-red-600 hover:underline"
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(null)}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeleting(sub.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>

              {expandedLogs.has(sub.id) && <DeliveryLog subId={sub.id} getToken={getToken} />}
            </div>
          ))}
        </div>
      )}

      {/* Signature verification guide */}
      <div className="mt-10 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <h2 className="mb-2 text-sm font-semibold text-slate-700">Verify signatures</h2>
        <p className="mb-3 text-xs text-slate-500">
          Every delivery includes{' '}
          <code className="rounded bg-slate-100 px-1">X-Develio-Signature</code> and{' '}
          <code className="rounded bg-slate-100 px-1">X-Develio-Timestamp</code> headers. Reject
          deliveries where the timestamp is more than 5 minutes old.
        </p>
        <pre className="overflow-x-auto rounded bg-slate-900 px-4 py-3 text-xs text-slate-200">
          {`import crypto from 'node:crypto';

function verify(body, timestamp, signature, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(\`\${timestamp}.\${body}\`)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(\`sha256=\${expected}\`),
    Buffer.from(signature),
  );
}`}
        </pre>
      </div>
    </div>
  );
}
